import { Modal } from '@mantine/core'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { CloseIcon, ChevronRightIcon } from '@forwardflow/icons'
import { useGetPhoneNumberPricing } from '../../queries/phone-number-management.queries'
import { PricingCardsSkeleton } from '../PhoneNumbersSkeleton'

interface ConfirmPurchaseNumbersModalProps {
   isOpen: boolean
   onClose: () => void
   onConfirm: () => void
   numberCount: number
   isLoading?: boolean
}

export const ConfirmPurchaseNumbersModal = ({
   isOpen,
   onClose,
   onConfirm,
   numberCount,
   isLoading = false,
}: ConfirmPurchaseNumbersModalProps) => {
   const [isTermsExpanded, setIsTermsExpanded] = useState(false)

   const { data: pricingData, isLoading: isLoadingPricing } =
      useGetPhoneNumberPricing(numberCount)

   const currentPeriod = pricingData?.currentPeriod
   const nextPeriod = pricingData?.nextPeriod

   const formatDate = (dateString: string) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
         day: 'numeric',
         month: 'short',
         year: 'numeric',
      })
   }

   return (
      <Modal
         opened={isOpen}
         onClose={onClose}
         withCloseButton={false}
         centered
         size="md"
         padding={0}
         styles={{
            content: {
               backgroundColor: 'rgba(0, 0, 0, 0.50)',
               backdropFilter: 'blur(8px)',
               borderRadius: '12px',
            },
            body: {
               padding: 0,
            },
         }}
      >
         <div className="flex flex-col p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
               <h2 className="text-white text-xl font-medium">
                  Add New Numbers
               </h2>
               <button
                  onClick={onClose}
                  className="text-white/70 hover:text-white transition-colors p-1"
               >
                  <CloseIcon className="w-5 h-5" />
               </button>
            </div>

            {/* Description */}
            <div className="mb-6">
               <p className="text-white/90 text-sm">
                  A moderate rate for {numberCount} new phone number
                  {numberCount !== 1 ? 's' : ''} will be added to your Monthly
                  Subscription Plan as below
               </p>
            </div>

            {/* Pricing Section */}
            {isLoadingPricing ? (
               <PricingCardsSkeleton />
            ) : (
               <div className="space-y-3 mb-6">
                  {currentPeriod && (
                     <div className="bg-[#9061DA]/20 rounded-lg p-4 flex items-center justify-between">
                        <div>
                           <p className="text-white/70 text-sm">
                              Next Payment Cycle
                           </p>
                           <p className="text-white font-medium">
                              {formatDate(currentPeriod.paymentDate)}
                           </p>
                        </div>
                        <p className="text-white text-xl font-semibold">
                           +${currentPeriod.amount}
                        </p>
                     </div>
                  )}

                  {nextPeriod && (
                     <div className="bg-[#9061DA]/20 rounded-lg p-4 flex items-center justify-between">
                        <div>
                           <p className="text-white/70 text-sm">
                              Payment Cycle from
                           </p>
                           <p className="text-white font-medium">
                              {formatDate(nextPeriod.paymentDate)}
                           </p>
                        </div>
                        <p className="text-white text-xl font-semibold">
                           +${nextPeriod.amount}
                        </p>
                     </div>
                  )}
               </div>
            )}

            {/* Terms of Service */}
            <button
               onClick={() => setIsTermsExpanded(!isTermsExpanded)}
               className="w-full bg-[#2A2A3C] rounded-lg p-4 flex items-center justify-between mb-6 hover:bg-[#2A2A3C]/80 transition-colors"
            >
               <p className="text-white text-sm font-medium">
                  Terms of Service
               </p>
               <motion.div
                  animate={{ rotate: isTermsExpanded ? 270 : 0 }}
                  transition={{ duration: 0.2 }}
               >
                  <ChevronRightIcon className="text-white w-4 h-4" />
               </motion.div>
            </button>

            {isTermsExpanded && (
               <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 text-white/70 text-sm"
               >
                  <p className="mb-2">
                     By purchasing these numbers, you agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                     <li>Monthly billing for active phone numbers</li>
                     <li>Usage according to Fair Use Policy</li>
                     <li>Cancellation requires 30 days notice</li>
                  </ul>
               </motion.div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-x-3">
               <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-8 py-3 rounded-lg border border-white/20 text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
               >
                  Back
               </motion.button>
               <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 px-8 py-3 rounded-lg bg-[#9061DA] hover:bg-[#7E4EC9] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-x-2"
               >
                  {isLoading && (
                     <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                           duration: 1,
                           repeat: Infinity,
                           ease: 'linear',
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                     />
                  )}
                  {isLoading ? 'Processing...' : 'Add Numbers'}
               </motion.button>
            </div>
         </div>
      </Modal>
   )
}
